import { forwardRef, useEffect, useState } from "react";
import { FieldError } from "react-hook-form";
import {
  Field,
  FormFieldStatus,
  Hint,
  Label,
  Option,
  Select,
} from "@gocardless/flux-react";
import { t, Trans } from "@lingui/macro";

import { AnnualRevenue } from "../consts";

import { getInputErrorStatus } from "src/utils/inputValidation";
import { FieldLevelError } from "src/common/api";
import { CountryCodes } from "src/common/country";
import { countryToCurrency, currencyToSymbol } from "src/common/currencies";

interface AnnualRevenueDropdown {
  error?: FieldError;
  apiError?: FieldLevelError;
  geoValue?: string;
}

export const AnnualRevenueDropdown = forwardRef<
  HTMLSelectElement,
  AnnualRevenueDropdown
>(({ error, apiError, geoValue, ...field }, ref) => {
  const [currency, setCurrency] = useState("");
  useEffect(() => {
    setCurrency(currencyToSymbol[countryToCurrency(geoValue as CountryCodes)]);
  }, [geoValue]);

  const AnnualRevenueOptions = Object.values(AnnualRevenue).map((amount) => (
    <Option key={amount} value={`${amount} (${currency})`} data-testid={amount}>
      {`${amount} (${currency})`}
    </Option>
  ));

  return (
    <Field data-testid="annual_revenue">
      <Label htmlFor="annual_revenue">
        <Trans id="sign-up.form.annual_revenue.label">
          What is your annual turnover?
        </Trans>
      </Label>
      <Select
        id="annual_revenue"
        {...field}
        ref={ref}
        status={getInputErrorStatus(!!error)}
      >
        {[
          <Option key="default" value="">
            {t({
              id: "Please select",
              message: "Please select",
            })}
          </Option>,
          ...AnnualRevenueOptions,
        ]}
      </Select>
      {error && (
        <Hint status={FormFieldStatus.Danger}>
          <Trans id="sign-up.form.annual_revenue.field-required-warning">
            Annual turnover input is required
          </Trans>
        </Hint>
      )}
      {apiError && (
        <Hint status={FormFieldStatus.Danger}>{apiError.message}</Hint>
      )}
    </Field>
  );
});
